import { useEffect, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { modal } from "@/lib/modal";
import { KeyRound, ShieldAlert, ArrowRight, Car, ScanLine } from "lucide-react";

export default function MasterConfig() {
  const [novaMaster, setNovaMaster] = useState("");
  const [novaLib, setNovaLib] = useState("");
  const [vendedorPodeMarcar, setVendedorPodeMarcar] = useState(false);
  const [verif4dig, setVerif4dig] = useState(true);
  const [otpCadastro, setOtpCadastro] = useState(false);
  const [limitePadrao, setLimitePadrao] = useState(2);
  const [loadingFlag, setLoadingFlag] = useState(false);

  useEffect(() => {
    masterApi.get("/master/config-publica")
      .then((r) => {
        setVendedorPodeMarcar(!!r.data.vendedor_pode_marcar_app_driver);
        setVerif4dig(r.data.verificacao_4dig_ativo !== false);
        setOtpCadastro(!!r.data.otp_cadastro_ativo);
        if (r.data.limite_acesso_diario_padrao != null) setLimitePadrao(r.data.limite_acesso_diario_padrao);
      })
      .catch(() => {});
  }, []);

  const salvarLimite = async () => {
    setLoadingFlag(true);
    try {
      await masterApi.put("/master/config", { limite_acesso_diario_padrao: Number(limitePadrao) || 0 });
      modal.success(Number(limitePadrao) > 0
        ? `Limite padrão salvo: ${limitePadrao} acesso(s) por dia para todos os clientes`
        : "Limite padrão salvo: SEM limite diário");
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoadingFlag(false); }
  };

  const toggleFlag = async (next) => {
    setLoadingFlag(true);
    try {
      await masterApi.put("/master/config", { vendedor_pode_marcar_app_driver: next });
      setVendedorPodeMarcar(next);
      modal.success(next ? "Vendedor pode marcar Motorista de App no cadastro" : "Apenas o Master pode marcar Motorista de App");
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoadingFlag(false); }
  };

  const toggle4dig = async (next) => {
    setLoadingFlag(true);
    try {
      await masterApi.put("/master/config", { verificacao_4dig_ativo: next });
      setVerif4dig(next);
      modal.success(next ? "Verificação por 4 dígitos ativada" : "Verificação por 4 dígitos desativada (acesso só pela placa)");
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoadingFlag(false); }
  };

  const toggleOtpCadastro = async (next) => {
    setLoadingFlag(true);
    try {
      await masterApi.put("/master/config", { otp_cadastro_ativo: next });
      setOtpCadastro(next);
      modal.success(next ? "OTP por SMS no 1º cadastro ativado" : "OTP no 1º cadastro desativado");
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoadingFlag(false); }
  };

  const trocarMaster = async () => {
    if (novaMaster.length < 6) return modal.error("Mínimo 6 caracteres");
    try {
      await masterApi.post("/auth/master/alterar-senha", { senha_nova: novaMaster });
      modal.success("Senha master alterada");
      setNovaMaster("");
    } catch (e) { modal.error(apiError(e)); }
  };

  const trocarLib = async () => {
    if (novaLib.length < 4) return modal.error("Mínimo 4 caracteres");
    try {
      await masterApi.post("/auth/master/alterar-senha-liberacao", { senha_nova: novaLib });
      modal.success("Senha de contingência alterada");
      setNovaLib("");
    } catch (e) { modal.error(apiError(e)); }
  };

  const inputCls = "mt-1 w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-config">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Configurações</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Senhas e segurança</h1>
      </div>

      <section className="card-soft rounded-xl p-4 max-w-md" data-testid="config-app-driver-flag">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(180,83,9,0.12)" }}>
            <Car size={14} className="text-amber-700" />
          </div>
          <div>
            <div className="font-medium text-sm">Permissão Motorista de App</div>
            <div className="text-[11px] text-neutral-500">Vendedor pode marcar a categoria no cadastro do cliente</div>
          </div>
        </div>
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-[12px] text-neutral-700">
            {vendedorPodeMarcar ? <>Ativado — vendedores podem marcar <Car size={12} className="inline-block -mt-0.5" /> no cadastro</> : "Desativado — apenas o Master promove via painel"}
          </span>
          <input data-testid="cfg-vend-pode-marcar" type="checkbox" disabled={loadingFlag}
                 checked={vendedorPodeMarcar} onChange={(e) => toggleFlag(e.target.checked)}
                 className="w-5 h-5 accent-amber-700" />
        </label>
      </section>

      <section className="card-soft rounded-xl p-4 max-w-md space-y-3" data-testid="config-antifraude">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(5,150,105,0.12)" }}>
            <ScanLine size={14} className="text-emerald-700" />
          </div>
          <div>
            <div className="font-medium text-sm">Identificação e anti-fraude</div>
            <div className="text-[11px] text-neutral-500">Regras de acesso do cliente no atendimento</div>
          </div>
        </div>
        <label className="flex items-center justify-between gap-3 cursor-pointer border-t border-neutral-100 pt-3">
          <span className="text-[12px] text-neutral-700">
            <b>Verificação por 4 dígitos</b><br />
            {verif4dig
              ? "Ativado — após a placa, o cliente confirma os 4 últimos dígitos do telefone"
              : "Desativado — acesso liberado só pela placa (sem os 4 dígitos)"}
          </span>
          <input data-testid="cfg-verif-4dig" type="checkbox" disabled={loadingFlag}
                 checked={verif4dig} onChange={(e) => toggle4dig(e.target.checked)}
                 className="w-5 h-5 accent-emerald-700" />
        </label>
        <label className="flex items-center justify-between gap-3 cursor-pointer border-t border-neutral-100 pt-3">
          <span className="text-[12px] text-neutral-700">
            <b>OTP por SMS no 1º cadastro</b><br />
            {otpCadastro
              ? "Ativado — no primeiro cadastro é enviado um código por SMS para validar o telefone"
              : "Desativado — cadastro sem código de validação"}
          </span>
          <input data-testid="cfg-otp-cadastro" type="checkbox" disabled={loadingFlag}
                 checked={otpCadastro} onChange={(e) => toggleOtpCadastro(e.target.checked)}
                 className="w-5 h-5 accent-emerald-700" />
        </label>
        <div className="border-t border-neutral-100 pt-3">
          <div className="text-[12px] text-neutral-700 mb-1.5">
            <b>Limite de abastecimentos por dia (todos os clientes)</b><br />
            Regra geral aplicada a toda a base. 0 = sem limite. O limite individual no cadastro do cliente, quando definido, prevalece.
          </div>
          <div className="flex gap-1.5">
            <input data-testid="cfg-limite-padrao" type="number" min={0} value={limitePadrao}
                   onChange={(e) => setLimitePadrao(parseInt(e.target.value) || 0)}
                   className="w-24 h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/15 transition" />
            <button data-testid="cfg-limite-salvar" onClick={salvarLimite} disabled={loadingFlag}
                    className="btn-primary-blue h-10 px-4 text-[12px] font-medium">
              Salvar limite
            </button>
          </div>
        </div>
      </section>

      <section className="card-soft rounded-xl p-4 max-w-md" data-testid="config-master-pwd">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(10,42,115,0.10)" }}>
            <KeyRound size={14} className="text-[color:var(--brand-blue)]" />
          </div>
          <div>
            <div className="font-medium text-sm">Senha Master</div>
            <div className="text-[11px] text-neutral-500">Acesso ao painel administrativo</div>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Nova senha (mín. 6)</label>
          <input data-testid="master-senha-nova" type="password" value={novaMaster}
                 onChange={(e) => setNovaMaster(e.target.value)} className={inputCls} />
        </div>
        <button data-testid="master-senha-btn" onClick={trocarMaster}
                className="w-full mt-3 h-10 btn-primary-blue text-[12px] font-medium flex items-center justify-center gap-1.5">
          Alterar senha master <ArrowRight size={12} />
        </button>
      </section>

      <section className="card-soft rounded-xl p-4 max-w-md" data-testid="config-lib-pwd">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(223,31,37,0.10)" }}>
            <ShieldAlert size={14} className="text-[#C0452A]" />
          </div>
          <div>
            <div className="font-medium text-sm">Senha de Liberação Especial</div>
            <div className="text-[11px] text-neutral-500">Contingência — todo uso é auditado</div>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Nova senha (mín. 4)</label>
          <input data-testid="lib-nova-senha" type="password" value={novaLib}
                 onChange={(e) => setNovaLib(e.target.value)} className={inputCls} />
        </div>
        <button data-testid="lib-senha-btn" onClick={trocarLib}
                className="w-full mt-3 h-10 btn-accent text-[12px] font-medium flex items-center justify-center gap-1.5">
          Alterar contingência <ArrowRight size={12} />
        </button>
      </section>
    </div>
  );
}
